<template>
  <v-dialog :value="value" max-width="700" @input="$emit('input', $event)">
    <v-card :loading="isLoading">
      <v-card-title class="text-h6">
        {{ $t("FIELD_TAGS.UPDATE_PARENT_CHILDREN_TITLE") }}
      </v-card-title>
      <v-card-text>
        <div class="mb-4">
          {{ $t("FIELD_TAGS.CURRENT_TAG") }}:
          <strong>{{ fieldTag ? fieldTag.value : "-" }}</strong>
        </div>

        <FieldTagsInput
          :value="selectedParentId"
          value-mode="id"
          :multiple="false"
          :field-tag-type-id="resolvedFieldTagTypeId"
          :exclude-ids="excludedTagIds"
          outlined
          dense
          :label="$t('FIELD_TAGS.PARENT')"
          @input="selectedParentId = $event"
        />

        <FieldTagsInput
          :value="selectedChildrenIds"
          value-mode="id"
          :field-tag-type-id="resolvedFieldTagTypeId"
          :exclude-ids="excludedTagIds"
          outlined
          dense
          :label="$t('FIELD_TAGS.CHILDREN')"
          @input="selectedChildrenIds = $event || []"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">{{ $t("FIELD_TAGS.CANCEL_BUTTON") }}</v-btn>
        <v-btn
          color="success"
          :disabled="!canSave"
          :loading="isSaving"
          @click="submit"
        >
          {{ $t("GENERAL.LABELS.SAVE") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { SHOW_SNACK } from "@/store/snack.module";
import FieldTagsInput from "@/views/partials/form/FieldTagsInput.vue";
import {
  GET_FIELD_TAG,
  UPDATE_FIELD_TAG_PARENT_CHILDREN,
} from "@/store/fieldTags.module";

export default {
  name: "UpdateParentChildrenDialog",
  components: {
    FieldTagsInput,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    fieldTag: {
      type: Object,
      default: null,
    },
    fieldTagTypeId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      selectedParentId: null,
      selectedChildrenIds: [],
      isLoading: false,
      isSaving: false,
    };
  },
  computed: {
    canSave() {
      return Boolean(this.fieldTag) && !this.isLoading && !this.isSaving;
    },
    resolvedFieldTagTypeId() {
      return this.fieldTag?.field_tag_type_id || this.fieldTagTypeId;
    },
    excludedTagIds() {
      return this.fieldTag?.id ? [Number(this.fieldTag.id)] : [];
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.loadData();
        return;
      }

      this.resetState();
    },
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];
        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }
      if (error && error.message) {
        return error.message;
      }
      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    resetState() {
      this.selectedParentId = null;
      this.selectedChildrenIds = [];
      this.isLoading = false;
      this.isSaving = false;
    },
    close() {
      this.$emit("input", false);
    },
    loadData() {
      if (!this.fieldTag) {
        return;
      }

      this.resetState();
      this.isLoading = true;

      this.$store
        .dispatch(GET_FIELD_TAG, this.fieldTag.id)
        .then((response) => {
          const parentId = response.parent_id ?? response.parent?.id ?? null;
          const children = response.children_ids || response.children || [];

          this.selectedParentId = parentId === null ? null : Number(parentId);
          this.selectedChildrenIds = children
            .map((child) => (typeof child === "object" ? child.id : child))
            .filter((id) => id !== null && id !== undefined)
            .map(Number);
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
          this.close();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    submit() {
      if (!this.canSave) {
        return;
      }

      this.isSaving = true;
      this.$store
        .dispatch(UPDATE_FIELD_TAG_PARENT_CHILDREN, {
          id: this.fieldTag.id,
          parent_id: this.selectedParentId,
          children_ids: this.selectedChildrenIds,
        })
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
          this.$emit("updated");
          this.close();
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>
